import { useCallback } from 'react';

import { DEFAULT_INSIGHT_PIN_LAYOUT } from '@aline/common';
import type { WorkflowViewState } from '@aline/common';
import { link, params } from '@aline/routes';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { Apollo } from 'src/gql/generated';

/**
 * Creates a Personal View and its first Chart placement, then opens the builder.
 * Resolves with the new View's id, or `null` once the failure has been surfaced.
 */
export function useCreateDraftViewAndOpenBuilder() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [createWorkflowView, { loading }] = Apollo.useCreateWorkflowViewMutation();

  const createDraftViewAndOpenBuilder = useCallback(
    async (options: {
      chartId: string;
      failureMessage: string;
      name: string;
      state: WorkflowViewState;
    }): Promise<string | null> => {
      try {
        const { data, error } = await createWorkflowView({
          variables: {
            input: {
              initialPin: {
                chartId: options.chartId,
                layout: DEFAULT_INSIGHT_PIN_LAYOUT,
                scope: Apollo.InsightScope.Personal,
              },
              name: options.name,
              showInSavedViews: false,
              state: options.state,
            },
          },
        });
        const viewId = data?.createWorkflowView.workflowView?.id;
        if (!viewId) throw error ?? new Error('The chart view was not created.');

        navigate(
          `${link.WorkflowInsightBuilder({ viewId })}?${params.insightChart}=${options.chartId}`
        );
        return viewId;
      } catch (error) {
        enqueueSnackbar(error instanceof Error ? error.message : options.failureMessage, {
          variant: 'error',
        });
        return null;
      }
    },
    [createWorkflowView, enqueueSnackbar, navigate]
  );

  return { createDraftViewAndOpenBuilder, loading };
}
